
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Feedback, FeedbackStatus, FeedbackType } from "@/components/dashboard/feedback/types";

export function AdminFeedbackTable() {
  const [sortField, setSortField] = useState<keyof Feedback>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query key maakt consistent gebruik van sortField en sortDirection
  const queryKey = ["admin-feedback", sortField, sortDirection];

  const { data: feedback = [], isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback")
        .select("*")
        .order(sortField, { ascending: sortDirection === "asc" });

      if (error) throw error;
      return data as Feedback[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      try {
        const { error } = await supabase
          .from("feedback")
          .delete()
          .eq("id", id);
        
        if (error) {
          console.error("Delete error:", error);
          throw new Error(`Kon het feedback item niet verwijderen: ${error.message}`);
        }
        
        return id;
      } catch (err) {
        console.error("Caught error:", err);
        throw err;
      }
    },
    onSuccess: (deletedId) => {
      // Optimistische update: verwijder het item uit de cache
      queryClient.setQueryData(queryKey, (oldData: Feedback[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(item => item.id !== deletedId);
      });
      
      // Invalideer de query om een volledige refresh te triggeren
      queryClient.invalidateQueries({ queryKey: queryKey });
      
      toast({
        title: "Feedback verwijderd",
        description: "Het feedback item is succesvol verwijderd.",
      });
      
      setIsDeleteDialogOpen(false);
      setFeedbackToDelete(null);
    },
    onError: (error) => {
      console.error("Error in onError callback:", error);
      toast({
        title: "Fout bij verwijderen",
        description: error instanceof Error ? error.message : "Er is een fout opgetreden bij het verwijderen van het feedback item.",
        variant: "destructive",
      });
      setIsDeleteDialogOpen(false);
    },
  });

  const handleDelete = (item: Feedback) => {
    setFeedbackToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (feedbackToDelete) {
      deleteMutation.mutate(feedbackToDelete.id);
    }
  };

  const getStatusBadgeColor = (status: FeedbackStatus) => {
    switch (status) {
      case "new":
        return "bg-blue-500";
      case "under_review":
        return "bg-yellow-500";
      case "planned":
        return "bg-green-500";
      case "completed":
        return "bg-purple-500";
      case "declined":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getTypeBadgeColor = (type: FeedbackType) => {
    switch (type) {
      case "bug":
        return "bg-red-500";
      case "feature":
        return "bg-green-500";
      case "general":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  if (isLoading) {
    return <div>Laden...</div>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Titel</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Naam</TableHead>
            <TableHead>E-mail</TableHead>
            <TableHead>Datum</TableHead>
            <TableHead className="text-right">Acties</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {feedback.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.title}</TableCell>
              <TableCell>
                <Badge className={getTypeBadgeColor(item.type)}>
                  {item.type}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge className={getStatusBadgeColor(item.status)}>
                  {item.status}
                </Badge>
              </TableCell>
              <TableCell>{item.submitter_name || "-"}</TableCell>
              <TableCell>{item.submitter_email || "-"}</TableCell>
              <TableCell>
                {new Date(item.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(item)}
                        className="text-[#1b9d3d] hover:text-[#1b9d3d]/80 hover:bg-green-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Andere functies</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Weet je het zeker?</AlertDialogTitle>
            <AlertDialogDescription>
              {feedbackToDelete && (
                <>
                  Je staat op het punt om het feedback item "{feedbackToDelete.title}" te verwijderen.
                  Deze actie kan niet ongedaan worden gemaakt.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
